import { useRef, useState } from 'react'
import { api, ApiError } from '../api/client'
import { copyText } from '../lib/clipboard'
import {
  ALL_TLDS,
  CHARSET_OPTIONS,
  COMPOSE_DEFAULTS,
  HUNTLY_MAX_BATCH,
  HUNTLY_MESSAGES,
  HUNTLY_QUERY_CHUNK,
  MERCHANTS,
  chunk,
  compose,
  countStatuses,
  exportLabel,
  exportSelection,
  isDomain,
  merchantUrl,
  parseLines,
  sanitizeSld,
  statusLabel,
  type CheckResult,
  type ComposeOpts,
} from '../lib/huntly'

type Tab = 'compose' | 'exact' | 'tld'

const SHOW_LIMIT = 300

export function Huntly() {
  const [tab, setTab] = useState<Tab>('compose')
  const [form, setForm] = useState<ComposeOpts>(COMPOSE_DEFAULTS)
  const [keyword, setKeyword] = useState('')
  const [exact, setExact] = useState('')
  const [tlds, setTlds] = useState<string[]>(['com', 'io', 'ai', 'co', 'app'])
  const [candidates, setCandidates] = useState<string[]>([])
  const [results, setResults] = useState<Record<string, CheckResult>>({})
  const [filter, setFilter] = useState('all')
  const [hint, setHint] = useState('批量生成、按后缀筛选、精确搜索。判定先看 DNS，再用 RDAP 核验「可注册」。')
  const [progress, setProgress] = useState('')
  const [busy, setBusy] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  function patch<K extends keyof ComposeOpts>(key: K, value: ComposeOpts[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function stop() {
    abortRef.current?.abort()
    setHint('已停止。')
  }

  function replaceCandidates(list: string[], label: string) {
    const seen = new Set<string>()
    const unique: string[] = []
    for (const raw of list) {
      const domain = String(raw || '').trim().toLowerCase()
      if (!isDomain(domain) || seen.has(domain)) continue
      seen.add(domain)
      unique.push(domain)
      if (unique.length >= HUNTLY_MAX_BATCH) break
    }
    setCandidates(unique)
    setResults({})
    setProgress(unique.length ? `准备查询 ${unique.length} 个（${label}）` : '')
    return unique
  }

  async function queryDomains(domains: string[], label: string, controller: AbortController) {
    const bag: Record<string, CheckResult> = {}
    const batches = chunk(domains, HUNTLY_QUERY_CHUNK)
    let cursor = 0
    let done = 0
    let failed = 0
    async function worker() {
      while (cursor < batches.length) {
        if (controller.signal.aborted) return
        const batch = batches[cursor]
        cursor += 1
        try {
          const payload = await api.post<{ results: CheckResult[]; invalid: string[]; source: string }>(
            '/api/huntly/check',
            { domains: batch },
            undefined,
            controller.signal,
          )
          for (const row of payload.results || []) bag[row.domain] = row
          setResults({ ...bag })
        } catch (error) {
          if (controller.signal.aborted || (error as Error).name === 'AbortError') return
          failed += 1
          for (const domain of batch) {
            if (!bag[domain]) bag[domain] = { domain, status: 'unknown', verified: false }
          }
          setResults({ ...bag })
          if (error instanceof ApiError && error.code === 'RATE_LIMITED') {
            setHint(error.message)
            controller.abort()
            return
          }
        }
        done += batch.length
        setProgress(`${label} ${Math.min(done, domains.length)}/${domains.length}`)
      }
    }
    const width = Math.min(3, batches.length || 1)
    if (batches.length) await Promise.all(Array.from({ length: width }, () => worker()))
    if (controller.signal.aborted) setProgress(`${label}（已停止）`)
    else if (failed) setProgress(`${label}（${failed} 批查询失败，已标为未知）`)
    else setProgress(`${label} 完成`)
    return bag
  }

  async function runQuery(list: string[], label: string) {
    const controller = new AbortController()
    abortRef.current = controller
    setBusy(true)
    try {
      return await queryDomains(list, label, controller)
    } finally {
      setBusy(false)
    }
  }

  async function onCompose() {
    const count = Math.min(HUNTLY_MAX_BATCH, Number(form.count) || 20)
    const outcome = compose({ ...form, count })
    if (!outcome.candidates.length) {
      setHint(HUNTLY_MESSAGES[outcome.code] || '生成失败，请检查条件。')
      return
    }
    const list = replaceCandidates(outcome.candidates, '条件组成')
    setHint(
      `已生成 ${list.length} 个（字符集：${outcome.charset}），开始查询…` +
        (outcome.exhausted ? ' ' + HUNTLY_MESSAGES.E_EXHAUSTED : ''),
    )
    const bag = await runQuery(list, '组成查询')
    if (!abortRef.current?.signal.aborted) {
      const counts = countStatuses(bag, list)
      setHint(
        `查询完成：可注册 ${counts.available} 个，可能可注册 ${counts.maybe} 个，已注册 ${counts.registered} 个，未知 ${counts.unknown} 个。`,
      )
    }
  }

  async function onExact() {
    const domains = parseLines(exact).filter(isDomain)
    if (!domains.length) {
      setHint('精确搜索需要带后缀的完整域名，例如 brewly.com。')
      return
    }
    const list = replaceCandidates(domains, '精确搜索')
    setHint(`开始查询 ${list.length} 个域名…`)
    await runQuery(list, '精确查询')
    if (!abortRef.current?.signal.aborted) setHint(`精确搜索完成，共 ${list.length} 条。`)
  }

  async function onTld() {
    const words = parseLines(keyword).map(sanitizeSld).filter(Boolean)
    if (!words.length) {
      setHint('先输入品牌词。')
      return
    }
    if (!tlds.length) {
      setHint('先勾选至少一个 TLD。')
      return
    }
    const domains: string[] = []
    for (const word of words) {
      for (const tld of tlds) {
        domains.push(word + '.' + tld)
        if (domains.length >= HUNTLY_MAX_BATCH) break
      }
      if (domains.length >= HUNTLY_MAX_BATCH) break
    }
    const list = replaceCandidates(domains, '按TLD筛选')
    setHint(`开始查询 ${list.length} 个域名…`)
    await runQuery(list, 'TLD 查询')
    if (!abortRef.current?.signal.aborted) setHint(`TLD 查询完成，共 ${list.length} 条。`)
  }

  const counts = countStatuses(results, candidates)
  const shown = candidates.filter((domain) => filter === 'all' || statusLabel(results[domain]).tone === filter)
  const exportName = exportLabel(filter)
  const exportList = exportSelection(candidates, results, filter)
  const visible = shown.slice(0, SHOW_LIMIT)

  async function copyExport() {
    if (!exportList.length) {
      setHint(`当前没有${exportName}的域名可复制。`)
      return
    }
    const ok = await copyText(exportList.join('\n'))
    setHint(ok ? `已复制 ${exportList.length} 个${exportName}域名。` : '浏览器拒绝了剪贴板访问，请改用下载。')
  }

  function downloadExport() {
    if (!exportList.length) {
      setHint(`当前没有${exportName}的域名可下载。`)
      return
    }
    const blob = new Blob([exportList.join('\n') + '\n'], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `huntly-${exportName}.txt`
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
    setHint(`已下载 ${exportList.length} 个${exportName}域名。`)
  }

  async function openMerchant(id: string) {
    const meta = MERCHANTS.find((item) => item.id === id)
    if (!meta) return
    const domains = candidates.filter((domain) => {
      const tone = statusLabel(results[domain]).tone
      if (meta.kind === 'available') return tone === 'available' || tone === 'maybe'
      return tone === meta.kind
    })
    if (!domains.length) {
      setHint(meta.label + '：当前没有' + (meta.kind === 'available' ? '可注册' : '已注册') + '的域名可带走，先查一批出来。')
      return
    }
    await copyText(domains.join('\n'))
    window.open(merchantUrl(id, domains), '_blank', 'noopener,noreferrer')
    setHint(`已复制 ${domains.length} 个域名并打开 ${meta.label} 的搜索页。`)
  }

  return (
    <section className="huntly-page">
      <header className="page-head">
        <div>
          <h1>猎域</h1>
          <p className="muted">批量生成、按后缀筛选、精确搜索，边查边出结果。判定先看 DNS，再用 RDAP 向注册局核验「可注册」那一批。</p>
        </div>
      </header>
      <p className="muted">
        可注册 {counts.available} · 可能可注册 {counts.maybe} · 已注册 {counts.registered} · 未知 {counts.unknown}
      </p>
      <div className="row">
        <button type="button" className={tab === 'compose' ? 'primary' : ''} onClick={() => setTab('compose')}>
          条件组成
        </button>
        <button type="button" className={tab === 'exact' ? 'primary' : ''} onClick={() => setTab('exact')}>
          精确搜索
        </button>
        <button type="button" className={tab === 'tld' ? 'primary' : ''} onClick={() => setTab('tld')}>
          按TLD筛选
        </button>
      </div>

      {tab === 'compose' && (
        <section className="card">
          <p className="muted">按字符集、位数和插入规则批量生成，生成后自动进入查询队列。</p>
          <div className="compose-grid">
            <div className="field">
              <span>字符类型</span>
              <div className="row wrap">
                {CHARSET_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={form.charsetType === option.value ? 'primary' : ''}
                    onClick={() => patch('charsetType', option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <label className="field">
              <span>自定义字符</span>
              <input value={form.customChars} placeholder="选「自定义」后生效" onChange={(e) => patch('customChars', e.target.value)} />
            </label>
            <label className="field">
              <span>过滤字符</span>
              <input value={form.filterChars} placeholder="不要这些字符" onChange={(e) => patch('filterChars', e.target.value)} />
            </label>
            <div className="field">
              <span>插入语句</span>
              <div className="row wrap">
                <input value={form.insertText} placeholder="例如 -shop 或 vip" onChange={(e) => patch('insertText', e.target.value)} />
                {(['head', 'middle', 'tail'] as const).map((pos) => (
                  <button key={pos} type="button" className={form.insertPos === pos ? 'primary' : ''} onClick={() => patch('insertPos', pos)}>
                    {pos === 'head' ? '头部' : pos === 'middle' ? '中部' : '尾部'}
                  </button>
                ))}
                <button type="button" className={form.insertArmed ? 'primary' : ''} onClick={() => patch('insertArmed', !form.insertArmed)}>
                  {form.insertArmed ? '已启用插入' : '启用插入'}
                </button>
              </div>
            </div>
            <div className="field">
              <span>生成排序</span>
              <div className="row">
                {(['random', 'asc', 'desc'] as const).map((mode) => (
                  <button key={mode} type="button" className={form.sort === mode ? 'primary' : ''} onClick={() => patch('sort', mode)}>
                    {mode === 'random' ? '随机' : mode === 'asc' ? '升序' : '降序'}
                  </button>
                ))}
              </div>
            </div>
            <label className="field">
              <span>后缀</span>
              <input value={form.suffix} placeholder=".com" onChange={(e) => patch('suffix', e.target.value)} />
            </label>
            <label className="field">
              <span>随机部分位数</span>
              <input type="number" min={1} max={20} value={form.length} onChange={(e) => patch('length', Number(e.target.value) || 5)} />
            </label>
            <label className="field">
              <span>个数</span>
              <input
                type="number"
                min={1}
                max={HUNTLY_MAX_BATCH}
                value={form.count}
                onChange={(e) => patch('count', Number(e.target.value) || 20)}
              />
            </label>
          </div>
          <div className="row">
            <button type="button" className="primary" disabled={busy} onClick={() => void onCompose()}>
              立刻生成
            </button>
            <button type="button" disabled={!busy} onClick={stop}>
              停止
            </button>
            <button type="button" onClick={() => void copyExport()}>
              复制{exportName}
            </button>
            <button type="button" onClick={downloadExport}>
              下载{exportName}
            </button>
            <button
              type="button"
              onClick={() => {
                stop()
                setForm(COMPOSE_DEFAULTS)
                setHint('条件已恢复默认。')
              }}
            >
              重置
            </button>
          </div>
        </section>
      )}

      {tab === 'exact' && (
        <section className="card">
          <p className="muted">整词含后缀，例如 brewly.com，直接匹配查询。</p>
          <textarea rows={6} spellCheck={false} value={exact} placeholder={'brewly.com\nfoundry.io'} onChange={(e) => setExact(e.target.value)} />
          <div className="row">
            <button type="button" className="primary" disabled={busy} onClick={() => void onExact()}>
              批量搜索
            </button>
            <button type="button" disabled={!busy} onClick={stop}>
              停止
            </button>
          </div>
        </section>
      )}

      {tab === 'tld' && (
        <section className="card">
          <p className="muted">输入品牌词（一行一个或空格分隔），再勾选后缀批量查询。组合数上限 {HUNTLY_MAX_BATCH} 个。</p>
          <textarea rows={4} spellCheck={false} value={keyword} placeholder={'foundry\nbrewly'} onChange={(e) => setKeyword(e.target.value)} />
          <p className="muted">已选 TLD（{tlds.length}）</p>
          <div className="row wrap">
            <button type="button" onClick={() => setTlds(ALL_TLDS.slice())}>
              全选
            </button>
            <button
              type="button"
              onClick={() => setTlds(ALL_TLDS.filter((tld) => !tlds.includes(tld)))}
            >
              反选
            </button>
            <button type="button" onClick={() => setTlds([])}>
              清空
            </button>
          </div>
          <div className="tld-grid">
            {ALL_TLDS.map((tld) => (
              <label key={tld} className="tld-chip">
                <input
                  type="checkbox"
                  checked={tlds.includes(tld)}
                  onChange={(e) =>
                    setTlds((list) => (e.target.checked ? [...list, tld] : list.filter((item) => item !== tld)))
                  }
                />
                .{tld}
              </label>
            ))}
          </div>
          <div className="row">
            <button type="button" className="primary" disabled={busy} onClick={() => void onTld()}>
              批量搜索
            </button>
            <button type="button" disabled={!busy} onClick={stop}>
              停止
            </button>
          </div>
        </section>
      )}

      <p className="muted">{hint}</p>
      {progress && <p className="muted">{progress}</p>}

      <section className="card">
        <div className="row spread">
          <h2>商家跳转</h2>
        </div>
        <p className="muted">点击后复制对应状态的域名并打开该商家的搜索页。可注册的去注册商，已注册的去二手市场。</p>
        <div className="row wrap">
          {MERCHANTS.map((item) => (
            <button key={item.id} type="button" title={item.note} onClick={() => void openMerchant(item.id)}>
              {item.label}
            </button>
          ))}
        </div>
      </section>

      <section className="card">
        <div className="row spread">
          <h2>{candidates.length ? `${candidates.length} 个候选` : '等待生成'}</h2>
          <div className="row">
            <label className="field inline">
              <span>筛选</span>
              <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="all">全部</option>
                <option value="available">可注册（已核验）</option>
                <option value="maybe">可能可注册</option>
                <option value="registered">已注册</option>
                <option value="unknown">未知</option>
              </select>
            </label>
            <button type="button" onClick={() => void copyExport()}>
              复制{exportName}{exportList.length ? `（${exportList.length}）` : ''}
            </button>
            <button type="button" onClick={downloadExport}>
              下载{exportName}{exportList.length ? `（${exportList.length}）` : ''}
            </button>
          </div>
        </div>
        {visible.length === 0 ? (
          <p className="empty">
            {candidates.length ? '当前筛选下没有域名。换个筛选条件看看。' : '生成、精确搜索或按 TLD 搜索之后，这里会列出结果。'}
          </p>
        ) : (
          <div className="hunt-results">
            {visible.map((domain) => {
              const info = statusLabel(results[domain])
              return (
                <div key={domain} className="hunt-row">
                  <div>
                    <b>{domain}</b>
                    <div className="muted">{info.meta}</div>
                  </div>
                  <span className={`tone tone-${info.tone}`}>{info.label}</span>
                  <button type="button" onClick={() => void copyText(domain)}>
                    复制
                  </button>
                </div>
              )
            })}
            {shown.length > SHOW_LIMIT && <p className="muted">列表只显示前 {SHOW_LIMIT} 条，复制和下载仍包含当前筛选的全部结果。</p>}
          </div>
        )}
        <p className="muted">
          判定口径：DNS 查到 NS 或 SOA = 已注册；DNS 查不到且 RDAP 也查不到 = 可注册（已核验）；DNS 查不到但 RDAP 不可用 = 可能可注册；其余标未知。结果缓存 15 分钟。
        </p>
      </section>
    </section>
  )
}
